import { Big } from "big.js";
import { useMemo } from "react";
import { useCase } from "../../context/CaseContext.tsx";
import { DisbursementType, Stage } from "../../shared/CaseConfiguration.ts";
import { fixedAt, NOTHING_LOCKED, withOpenAmount } from "../../shared/costChanges.ts";
import { Budget, LockedCosts } from "../../shared/CostModel.ts";
import { convert } from "./BudgetUtils.ts";
import { CostsEditor } from "./CostsEditor.tsx";
import { EditableCostsPerStage } from "./EditableCostsPerStageTable.tsx";

const DISBURSEMENTS: DisbursementType[] = [
  "courtFee",
  "barristerFee",
  "expertFee",
  "otherDisbursement",
];

/**
 * A budget's costs as the Cost editor shows them, per stage: what settlements
 * dropped as fixed numbers, the rest in the inputs (R5 of
 * SettlementFreezeOnSave.test.ts).
 */
function getEditableCosts(
  budget: Budget,
  locked: LockedCosts,
): EditableCostsPerStage[] {
  return convert(budget)
    .filter(({ stage }) => stage !== Stage.Appeals)
    .map(({ stage, sum: { disbursements, lawyerCosts } }) => {
      const fixedOf = (type: DisbursementType | "lawFirmFee") =>
        fixedAt(locked, stage, type).toNumber();
      const rest = (amount: number, fixed: number) =>
        new Big(amount).minus(fixed).toNumber();
      const fixed = {
        lawyerCosts: fixedOf("lawFirmFee"),
        disbursements: Object.fromEntries(
          DISBURSEMENTS.map((type) => [type, fixedOf(type)]),
        ) as EditableCostsPerStage["disbursements"],
      };
      return {
        stage,
        lawyerCosts: rest(lawyerCosts, fixed.lawyerCosts),
        disbursements: Object.fromEntries(
          DISBURSEMENTS.map((type) => [
            type,
            rest(disbursements[type], fixed.disbursements[type]),
          ]),
        ) as EditableCostsPerStage["disbursements"],
        fixed,
      };
    });
}

export const CostsEditorBudgetAdapter = ({
  budget,
  otherBudgets = [],
  updateBudget,
}: {
  budget: Budget;
  otherBudgets: Budget[];
  updateBudget: (newValue: Budget) => Promise<void>;
}) => {
  const caseContext = useCase();
  const { parties, results, settlements } = caseContext.getCase();
  const apportionment = parties?.find(
    ({ id }) => id === budget.partyId,
  )?.apportionment;
  const locked = results?.lockedCosts?.[budget.partyId] ?? NOTHING_LOCKED;
  // where the party left the case, its costs are fixed (R11 of
  // SettlementFreezeOnSave.test.ts)
  const leftAt = settlements?.find(({ id }) => id === locked.exit)?.stage;
  const stages = caseContext
    .getStages()
    .filter((stage) => stage !== Stage.Appeals);
  const costs: EditableCostsPerStage[] = useMemo(
    () => getEditableCosts(budget, locked),
    [budget, locked],
  );

  // each input changed sets the open costs of its stage and type
  const updateCosts = (costUpdates: EditableCostsPerStage[]) => {
    let updated = budget;
    for (const update of costUpdates) {
      const current = costs.find(({ stage }) => stage === update.stage);
      const inputs: [DisbursementType | "lawFirmFee", number, number][] = [
        ["lawFirmFee", update.lawyerCosts, current?.lawyerCosts ?? 0],
        ...DISBURSEMENTS.map(
          (type) =>
            [
              type,
              update.disbursements[type] ?? 0,
              current?.disbursements[type] ?? 0,
            ] as [DisbursementType, number, number],
        ),
      ];
      for (const [type, amount, was] of inputs)
        if (amount !== was)
          updated = withOpenAmount(
            updated,
            locked,
            apportionment,
            stages,
            update.stage,
            type,
            amount,
          );
    }
    return updateBudget(updated);
  };

  const getCosts = (partyId: string) => {
    const partyBudget = otherBudgets.find(
      (budget) => budget.partyId === partyId
    );
    if (!partyBudget) {
      console.log(`No budget found for party '${partyId}'`);
      return;
    }

    // another party's costs, all of them, in the inputs
    return getEditableCosts(partyBudget, NOTHING_LOCKED).map(
      ({ fixed: _, ...inputs }) => ({
        ...inputs,
        fixed: costs.find(({ stage }) => stage === inputs.stage)?.fixed,
      }),
    );
  };

  return (
    <CostsEditor
      partyId={budget.partyId}
      leftAt={leftAt}
      costs={costs}
      updateCosts={updateCosts}
      getCosts={getCosts}
    />
  );
};
